import * as React from "react";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CountryItem {
  id: number;
  name: string;
  iso2: string;
}

interface CountrySelectProps {
  id?: string;
  value: string;
  onChange: (iso2: string, country?: CountryItem) => void;
  countries: CountryItem[];
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export default function CountrySelect({
  id = "country-select",
  value,
  onChange,
  countries,
  isLoading = false,
  disabled = false,
  error,
  className,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);

  const selectedCountry = React.useMemo(() => {
    if (!value) return null;
    return (
      countries.find(
        (c) =>
          c.iso2.toLowerCase() === value.toLowerCase() ||
          c.name.toLowerCase() === value.toLowerCase(),
      ) || null
    );
  }, [countries, value]);

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label="Seleziona Nazione"
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            disabled={disabled || isLoading}
            className={cn(
              "w-full justify-between h-10 px-3 bg-transparent text-left font-normal border-input hover:bg-accent/50",
              !selectedCountry && "text-muted-foreground",
              error && "border-destructive focus-visible:ring-destructive",
              className,
            )}
          >
            <div className="flex items-center gap-2.5 truncate">
              {selectedCountry?.iso2 ? (
                <span
                  className={`fi fi-${selectedCountry.iso2.toLowerCase()} rounded-xs shrink-0 text-base shadow-xs`}
                  aria-hidden="true"
                />
              ) : (
                <Globe
                  className="h-4 w-4 text-muted-foreground shrink-0 opacity-70"
                  aria-hidden="true"
                />
              )}
              <span className="truncate">
                {selectedCountry
                  ? selectedCountry.name
                  : "Seleziona Nazione..."}
              </span>
            </div>
            <ChevronsUpDown
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] max-w-[400px] z-50"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Cerca nazione o codice ISO..." />
            <CommandList className="max-h-60">
              <CommandEmpty>Nessuna nazione trovata.</CommandEmpty>
              <CommandGroup>
                {countries.map((country) => {
                  const isSelected =
                    selectedCountry?.id === country.id ||
                    country.iso2.toLowerCase() === value.toLowerCase();

                  return (
                    <CommandItem
                      key={country.id}
                      value={`${country.name} ${country.iso2}`}
                      onSelect={() => {
                        onChange(country.iso2.toUpperCase(), country);
                        setOpen(false);
                      }}
                      className="flex items-center justify-between gap-2 cursor-pointer py-2 px-3"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          className={`fi fi-${country.iso2.toLowerCase()} rounded-xs shrink-0 text-base shadow-xs`}
                          aria-hidden="true"
                        />
                        <span className="truncate font-medium">
                          {country.name}
                        </span>
                        <span className="text-xs text-muted-foreground uppercase">
                          ({country.iso2})
                        </span>
                      </div>
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0 text-primary transition-opacity",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden="true"
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
